import React, { useMemo } from 'react';
import { PropTypes } from 'prop-types';
import OrderStop from 'components/orders/OrderStop';
import { useOrder } from 'hooks/useOrders';
import OrderStopContextProvider from 'components/orders/OrderStop/UseOrderStop';
import { isEmpty } from 'utils/types';
import { ORDER_STATUSES } from 'utils/order/constants';
import { useTranslation } from 'react-i18next';
import DriverStatus from 'components/orders/DriverStatus';
import NonFeasibleEstimationMessage from 'components/NonFeasibleEstimationMessage';
import { useFeasibility } from 'hooks/useFeasibility';
import { isStatusBeforeAssigned } from 'utils/order/methods';

const ModalSummary = ({ className, orderId, isRecurring }) => {
  const { getIsOrderNonCore } = useFeasibility();
  const { t } = useTranslation(['ModalSummary', 'Catalog']);
  const { order } = useOrder(orderId, isRecurring);
  const firstStop = order.stops[0];

  const showFeasibleEstimationMessage = useMemo(() => {
    const isNonCore = getIsOrderNonCore(order);
    return (
      isNonCore &&
      typeof order.net_full_price !== 'undefined' &&
      isStatusBeforeAssigned(order.status)
    );
  }, [order, getIsOrderNonCore]);

  const extras = order.extras || [];
  const availableVehicleExtras = ['LIFTGATE', 'SIDE_LOAD', 'TOP_LOAD'];
  const vehicleExtras = extras.filter((extra) =>
    availableVehicleExtras.includes(extra)
  );

  return (
    <ul className={className} data-testid="modal-summary">
      {showFeasibleEstimationMessage && (
        <li>
          <NonFeasibleEstimationMessage price={order?.net_full_price} />
        </li>
      )}
      {vehicleExtras.length > 0 && (
        <div className="driver-info driver-info-vehicle-extras">
          <div className="driver-info__vehicles">
            {t('configuration.vehicle_details.header', {
              ns: 'Catalog',
            })}
            :{' '}
            {vehicleExtras
              .map((e) => e.toLowerCase())
              .map((extraKey) =>
                t(`configuration.vehicle_details.${extraKey}`, {
                  ns: 'Catalog',
                })
              )
              .join(', ')}
          </div>
        </div>
      )}
      {!isRecurring && (
        <DriverStatus order={order}>
          {!isEmpty(order.drivers) &&
            (order.status === ORDER_STATUSES.ASSIGNED ||
              order.status === ORDER_STATUSES.IN_TRANSIT) && (
              <div className="driver-info" data-testid="driver-info">
                <div className="driver-info__driver">
                  <span className="driver-info__driver-label">{`${t(
                    'OrderStop:label.driver.transporter'
                  )}: `}</span>
                  <span className="driver-info__driver-name">
                    {order.drivers.map((driver) => `${driver.name}`)}
                  </span>
                </div>
                <div className="driver-info__vehicles">
                  {order.vehicles
                    ?.map((vehicle) => `${vehicle.plate_number}`)
                    .join(' + ')}
                </div>
              </div>
            )}
        </DriverStatus>
      )}
      {order.stops?.map((stop, index) => (
        <OrderStopContextProvider
          key={index}
          order={order}
          stop={stop}
          firstStop={firstStop}
          orderStatus={order.status}
          isRecurring={isRecurring}
          isLastStop={index < order.stops.length - 1}
        >
          <OrderStop
            stops={order.stops}
            orderId={orderId}
            stopIndex={index}
            orderType={order.journey_type}
            isMultiRoute={order.stops.length >= 3}
            isRecurring={isRecurring}
          />
        </OrderStopContextProvider>
      ))}
    </ul>
  );
};

ModalSummary.propTypes = {
  orderId: PropTypes.string.isRequired,
  isRecurring: PropTypes.bool,
};

ModalSummary.defaultProps = {
  isRecurring: false,
};

export default ModalSummary;
